import { Component, forwardRef, Input, OnInit, OnDestroy } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatNativeDateModule } from '@angular/material/core';
import { CommonModule } from '@angular/common';
import { Subject, takeUntil } from 'rxjs';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { NgxMatTimepickerModule } from 'ngx-mat-timepicker';

// Initialize dayjs plugins
dayjs.extend(utc);
dayjs.extend(timezone);

interface TimeOption {
	value: string;
	display: string;
}

@Component({
	selector: 'app-date-time-picker',
	templateUrl: './date-time-picker.component.html',
	styleUrls: ['./date-time-picker.component.scss'],
	standalone: true,
	imports: [
		CommonModule,
		ReactiveFormsModule,
		MatDatepickerModule,
		MatFormFieldModule,
		MatInputModule,
		MatSelectModule,
		MatNativeDateModule,
		NgxMatTimepickerModule,
	],
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => DateTimePickerComponent),
			multi: true,
		},
	],
})
export class DateTimePickerComponent
	implements OnInit, OnDestroy, ControlValueAccessor
{
	@Input() startTime: string = '08:00';
	@Input() endTime: string = '23:30';
	@Input() defaultDateTime?: Date;
	@Input() placeholder: string = 'Select date and time';
	@Input() disabled: boolean = false;
	@Input() appearance: 'fill' | 'outline' = 'outline';

	dateControl = new FormControl<Date | null>(null);
	timeControl = new FormControl<string>({ value: '', disabled: true });
	timeOptions: TimeOption[] = [];
	minDate = new Date(); // Today as minimum date

	private readonly destroy$ = new Subject<void>();
	private onChange: (value: Date | null) => void = () => {};
	private onTouched: () => void = () => {};
	private defaultStartTime: string = '08:00';

	ngOnInit(): void {
		// Store the default start time for later restoration
		this.defaultStartTime = this.startTime;
		this.generateTimeOptions();
		this.setupValueChanges();

		// Set default value if provided
		if (this.defaultDateTime) {
			this.writeValue(this.defaultDateTime);
		}
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	writeValue(value: Date | null): void {
		console.log('writeValue called with:', value);
		if (value) {
			const dayjsDate = dayjs(value);
			console.log('Parsed date:', dayjsDate.format('YYYY-MM-DD HH:mm:ss'));
			
			this.dateControl.setValue(dayjsDate.toDate(), { emitEvent: false });

			// Format time as 12-hour format for ngx-mat-timepicker-field
			const timeString = dayjsDate.format('h:mm A');
			console.log('Setting time to:', timeString);
			this.timeControl.setValue(timeString, { emitEvent: false });
			this.timeControl.enable({ emitEvent: false });
		} else {
			this.dateControl.setValue(null, { emitEvent: false });
			this.timeControl.setValue('', { emitEvent: false });
			this.timeControl.disable({ emitEvent: false });
		}
	}

	registerOnChange(fn: (value: Date | null) => void): void {
		this.onChange = fn;
	}

	registerOnTouched(fn: () => void): void {
		this.onTouched = fn;
	}

	setDisabledState(isDisabled: boolean): void {
		this.disabled = isDisabled;
		if (isDisabled) {
			this.dateControl.disable({ emitEvent: false });
			this.timeControl.disable({ emitEvent: false });
		} else {
			this.dateControl.enable({ emitEvent: false });
			if (this.dateControl.value) {
				this.timeControl.enable({ emitEvent: false });
			}
		}
	}

	private setupValueChanges(): void {
		// Listen to date changes
		this.dateControl.valueChanges
			.pipe(takeUntil(this.destroy$))
			.subscribe((date) => {
				if (date) {
					this.timeControl.enable();

					// Only set default time if there's no existing time value
					if (!this.timeControl.value) {
						// Check if selected date is today
						const selectedDate = dayjs(date);
						const today = dayjs();

						if (selectedDate.isSame(today, 'day')) {
							// If today is selected, calculate next coming time interval
							const now = dayjs();
							const currentMinutes = now.minute();
							let nextInterval;

							if (currentMinutes <= 30) {
								nextInterval = now.hour(now.hour()).minute(30);
							} else {
								nextInterval = now.add(1, 'hour').minute(0);
							}

							// Make sure the time is within our time range
							const nextIntervalTime = nextInterval.format('HH:mm');
							const [endHour, endMinute] = this.endTime.split(':').map(Number);
							const endTime = dayjs().hour(endHour).minute(endMinute);

							if (nextInterval.isAfter(endTime)) {
								// If next interval is beyond end time, use default start time in 12-hour format
								const defaultTime12h = dayjs(this.defaultStartTime, 'HH:mm').format('h:mm A');
								this.timeControl.setValue(defaultTime12h);
							} else {
								// Use next interval time in 12-hour format
								const nextIntervalTime12h = nextInterval.format('h:mm A');
								this.timeControl.setValue(nextIntervalTime12h);
							}
						} else {
							// If another date is selected, use default start time in 12-hour format
							const defaultTime12h = dayjs(this.defaultStartTime, 'HH:mm').format('h:mm A');
							this.timeControl.setValue(defaultTime12h);
						}
					}
				} else {
					this.timeControl.disable();
					this.timeControl.setValue('');
				}
				this.updateValue();
			});

		// Listen to time changes
		this.timeControl.valueChanges
			.pipe(takeUntil(this.destroy$))
			.subscribe(() => {
				this.updateValue();
			});
	}

	private updateValue(): void {
		const date = this.dateControl.value;
		const time = this.timeControl.value;

		if (date && time) {
			let hours: number, minutes: number;
			
			// Handle different time formats from ngx-mat-timepicker-field
			if (typeof time === 'string') {
				// Parse time string (could be "HH:mm" or "h:mm AM/PM")
				const timeString = time.trim();
				if (timeString.includes('AM') || timeString.includes('PM')) {
					// Handle 12-hour format
					const [timePart, period] = timeString.split(' ');
					const [hourStr, minuteStr] = timePart.split(':');
					let hour = parseInt(hourStr, 10);
					minutes = parseInt(minuteStr, 10);
					
					if (period === 'PM' && hour !== 12) {
						hour += 12;
					} else if (period === 'AM' && hour === 12) {
						hour = 0;
					}
					hours = hour;
				} else {
					// Handle 24-hour format
					const [hourStr, minuteStr] = timeString.split(':');
					hours = parseInt(hourStr, 10);
					minutes = parseInt(minuteStr, 10);
				}
			} else {
				// If time is already a number or other format, try to extract hours/minutes
				console.warn('Unexpected time format:', time);
				return;
			}

			const combinedDateTime = dayjs(date)
				.hour(hours)
				.minute(minutes)
				.second(0)
				.millisecond(0);

			this.onChange(combinedDateTime.toDate());
		} else {
			this.onChange(null);
		}
		this.onTouched();
	}

	private generateTimeOptions(dynamicStartTime?: string): void {
		this.timeOptions = [];

		const effectiveStartTime = dynamicStartTime || this.startTime;
		const [startHour, startMinute] = effectiveStartTime.split(':').map(Number);
		const [endHour, endMinute] = this.endTime.split(':').map(Number);

		const startDayjs = dayjs().hour(startHour).minute(startMinute);
		const endDayjs = dayjs().hour(endHour).minute(endMinute);

		let current = startDayjs;

		// Generate options in 30-minute intervals
		while (current.isBefore(endDayjs) || current.isSame(endDayjs)) {
			const timeString = current.format('HH:mm'); // 24-hour format for internal value
			const displayString = current.format('h:mm A'); // 12-hour format for display
			this.timeOptions.push({
				value: timeString,
				display: displayString,
			});
			current = current.add(30, 'minute');
		}
	}
}
